import { useMemo, useState } from "react";
import type { AnswerSource } from "../types";
import { placesApi } from "../api/client";
import PlacesMap from "./PlacesMap";
import "./PlacesPanel.css";

interface Props {
  sources: AnswerSource[];
}

const VISIBLE_LIMIT = 3;

function formatCloseTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function PlacePhoto({ name }: { name: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      className="place-card-photo"
      src={placesApi.photoUrl(name, 160)}
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

function PlaceCard({
  source,
  active,
  onHover,
}: {
  source: AnswerSource;
  active?: boolean;
  onHover?: () => void;
}) {
  const p = source.place!;
  const closeTime = formatCloseTime(p.next_close_time);
  return (
    <a
      className={"place-card" + (active ? " place-card-active" : "")}
      onMouseEnter={onHover}
      href={p.maps_uri}
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      <div className="place-card-body">
        <div className="place-card-header">
          <span className="place-card-n">{source.n}</span>
          <span className="place-card-name">{p.name || source.title}</span>
        </div>
        {p.address && <div className="place-card-address">{p.address}</div>}
        {p.rating != null && (
          <div className="place-card-rating">
            <span className="place-star" aria-hidden="true">★</span>
            <span>{p.rating.toFixed(1)}</span>
            {p.review_count != null && <span className="place-card-reviews">({p.review_count})</span>}
          </div>
        )}
        {p.open_now != null && (
          <div className={"place-card-hours" + (p.open_now ? " place-card-open" : " place-card-closed")}>
            {p.open_now ? "Open now" : "Closed"}
            {p.open_now && closeTime ? ` · until ${closeTime}` : ""}
          </div>
        )}
      </div>
      {p.photos?.length > 0 && <PlacePhoto name={p.photos[0]} />}
    </a>
  );
}

export default function PlacesPanel({ sources }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [activeN, setActiveN] = useState<number | null>(null);

  const placeSources = useMemo(
    () => sources.filter((s) => s.place?.lat != null && s.place?.lng != null),
    [sources]
  );

  const points = useMemo(
    () =>
      placeSources.map((s) => ({
        n: s.n,
        lat: s.place!.lat as number,
        lng: s.place!.lng as number,
        title: s.place!.name || s.title,
      })),
    [placeSources]
  );

  if (placeSources.length < 1) return null;

  const visible = expanded ? placeSources : placeSources.slice(0, VISIBLE_LIMIT);
  const hiddenCount = placeSources.length - VISIBLE_LIMIT;

  return (
    <div className="places-panel">
      <div className="places-panel-main">
        <PlacesMap points={points} activeN={activeN} onSelect={setActiveN} />
        <div className="places-panel-cards">
          {visible.map((s) => (
            <PlaceCard
              key={s.n}
              source={s}
              active={s.n === activeN}
              onHover={() => setActiveN(s.n)}
            />
          ))}
        </div>
      </div>
      {!expanded && hiddenCount > 0 && (
        <button type="button" className="places-panel-more" onClick={() => setExpanded(true)}>
          See more places
        </button>
      )}
    </div>
  );
}
